import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type ReactNode } from 'react';
import { Badge, Button, Card, Flex, HeaderItalic, SectionCard, Text } from '@/components/common';
import { PageTopBar } from '@/components/layout';
import { FavoriteButton } from '@/components/profile/FavoriteButton';
import { PhotoLikeButton } from '@/components/profile/PhotoLikeButton';
import { ProfilePhoto } from '@/components/profile/ProfilePhoto';
import { ProfilePhotoCarousel } from '@/components/profile/ProfilePhotoCarousel';
import { SafetyDialog } from '@/components/profile/SafetyDialog';
import { ShareProfileDialog } from '@/components/profile/ShareProfileDialog';
import { UserDistanceLabel } from '@/components/profile/UserDistanceLabel';
import { INTENTIONS, type IntentionId } from '@/constants/intentions';
import { useCurrentUser } from '@/contexts';
import { useSentLikes } from '@/hooks';
import { cn } from '@/lib/utils';
import { calculateAge, type ProfilePhoto as ProfilePhotoModel, type UserProfile } from '@/types';
import { firstNameOf, normalizePhotos } from '@/utils';

interface ProfilePageProps {
  user: UserProfile;
  isOwn: boolean;
  onBack: () => void;
  onMessage?: () => void;
}

function isIntentionId(value: string): value is IntentionId {
  return INTENTIONS.some(option => option.id === value);
}

function asList(value: string | string[] | undefined): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return value.trim() ? [value] : [];
}

function PillRow({ values, highContrast = false }: { values: string[]; highContrast?: boolean }) {
  if (values.length === 0) return null;
  return (
    <Flex wrap="wrap" gap="2">
      {values.map(value =>
        highContrast ? (
          <Badge key={value} color="gray" highContrast size="1">
            {value}
          </Badge>
        ) : (
          <Badge key={value} color="black" variant="outline" size="1">
            {value}
          </Badge>
        )
      )}
    </Flex>
  );
}

function FieldGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Flex direction="column" gap="2">
      <Text size="2" color="gray">
        {label}
      </Text>
      {children}
    </Flex>
  );
}

/** Uniform horizontal padding for cards between full-bleed photos. */
function Padded({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <div className="px-5">{children}</div>;
}

export function ProfilePage({ user, isOwn, onBack, onMessage }: ProfilePageProps) {
  const { t } = useTranslation('profile');
  const { t: tOnboarding } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { user: authUser } = useCurrentUser();
  const {
    isLiked,
    isPending,
    toggleLike,
    isBlocked,
    isLoading: likesLoading,
  } = useSentLikes(authUser, isOwn ? undefined : user.uid);

  const age = user.age ?? calculateAge(user.birthday);
  const nameAge = age != null ? t('view.nameAge', { name: user.name, age }) : user.name;

  const theirIntentions = (user.intentions ?? [])
    .filter(isIntentionId)
    .map(id => INTENTIONS.find(option => option.id === id))
    .filter((option): option is (typeof INTENTIONS)[number] => Boolean(option));

  const intentionLabels = theirIntentions.map(option => tOnboarding(option.labelKey));

  const openTo = asList(user.openTo);
  const genders = asList(user.gender);
  const pronouns = asList(user.pronouns);
  const orientation = asList(user.orientation);
  const languages = asList(user.languages);
  const ethnicity = asList(user.ethnicity);
  const interests = asList(user.interests);
  const spots = asList(user.spots);
  const kinks = asList(user.intimacy);
  const relationshipStatus = asList(user.relationshipStatus);
  const relationshipTypes = asList(user.relationshipTypes);
  const city = user.city?.trim() ? [user.city] : [];
  const height = user.height != null ? [t('view.heightValue', { height: user.height })] : [];
  const bio = user.bio?.trim() ?? '';
  const starSign = user.zodiac?.trim() ?? '';

  const showMyState = intentionLabels.length > 0 || openTo.length > 0;
  const showIdentity = genders.length > 0 || pronouns.length > 0 || orientation.length > 0;
  const showWhere = relationshipStatus.length > 0 || relationshipTypes.length > 0;
  const showWorld =
    interests.length > 0 ||
    spots.length > 0 ||
    kinks.length > 0 ||
    languages.length > 0 ||
    ethnicity.length > 0;
  const showBasics = city.length > 0 || height.length > 0 || starSign.length > 0;

  const photos = normalizePhotos(user.photos);
  // Interleaved static slots (v90): only when that photo index exists.
  // Hero carousel still lets you swipe through every photo.
  const photoBeforeIdentity = photos[1];
  const photoAfterWhere = photos[2];
  const photoAfterWorld = photos[3];
  const photoAtEnd = photos[4];
  const firstName = firstNameOf(user.name);

  const renderLikeOverlay =
    !isOwn && !isBlocked && !likesLoading
      ? (photo: ProfilePhotoModel) => (
          <PhotoLikeButton
            photo={photo}
            liked={isLiked(photo.id)}
            pending={isPending(photo.id)}
            onToggle={() => void toggleLike(photo)}
          />
        )
      : undefined;

  const headerAction = isOwn ? (
    <Flex align="center" gap="2">
      <ShareProfileDialog uid={user.uid} name={firstName} />
      <Button
        type="button"
        variant="outline"
        color="gray"
        highContrast
        radius="full"
        size="sm"
        fullWidth={false}
        onClick={() => navigate('/account/my-profile/edit')}
      >
        {t('view.actions.edit')}
      </Button>
    </Flex>
  ) : (
    <Flex align="center" gap="2">
      {!isBlocked && !likesLoading && <FavoriteButton uid={user.uid} />}
      <SafetyDialog name={firstName} uid={user.uid} />
    </Flex>
  );

  return (
    <Flex direction="column" className="h-full min-h-0">
      <PageTopBar backButton onBack={onBack} endSlot={headerAction} />
      <Flex direction="column" className={cn('min-h-0 flex-1 overflow-y-auto', 'gap-7 pb-5')}>
        <Flex direction="column" gap="1" minWidth="0" px="4">
          <HeaderItalic>{nameAge}</HeaderItalic>
          {!isOwn && <UserDistanceLabel user={user} variant="ghost" />}
          {user.lastActive && (
            <Text size="1" color="gray">
              {t('view.seen', { when: user.lastActive })}
            </Text>
          )}
        </Flex>
        <ProfilePhotoCarousel
          photos={user.photos}
          name={user.name}
          renderOverlay={renderLikeOverlay}
        />

        {showMyState && (
          <Padded>
            <Card variant="accented">
              <Flex direction="column" gap="4">
                <HeaderItalic color="red" size="4">
                  {t('view.sections.myState')}
                </HeaderItalic>
                {intentionLabels.length > 0 && (
                  <FieldGroup label={t('view.fields.intention')}>
                    <PillRow values={intentionLabels} highContrast />
                  </FieldGroup>
                )}
                {openTo.length > 0 && (
                  <FieldGroup label={t('view.fields.openTo')}>
                    <PillRow values={openTo} highContrast />
                  </FieldGroup>
                )}
              </Flex>
            </Card>
          </Padded>
        )}

        {bio && (
          <Padded>
            <SectionCard title={t('view.sections.intro')}>
              <Text as="p" size="2" style={{ fontStyle: 'italic', lineHeight: 1.55 }}>
                {t('view.introQuote', { bio })}
              </Text>
            </SectionCard>
          </Padded>
        )}

        <ProfilePhoto
          src={photoBeforeIdentity?.url}
          name={user.name}
          index={2}
          overlay={photoBeforeIdentity && renderLikeOverlay?.(photoBeforeIdentity)}
        />

        {showIdentity && (
          <Padded>
            <SectionCard title={t('view.sections.identity')}>
              <Flex direction="column" gap="4">
                {genders.length > 0 && (
                  <FieldGroup label={t('view.fields.gender')}>
                    <PillRow values={genders} />
                  </FieldGroup>
                )}
                {pronouns.length > 0 && (
                  <FieldGroup label={t('view.fields.pronouns')}>
                    <PillRow values={pronouns} />
                  </FieldGroup>
                )}
                {orientation.length > 0 && (
                  <FieldGroup label={t('view.fields.orientation')}>
                    <PillRow values={orientation} />
                  </FieldGroup>
                )}
              </Flex>
            </SectionCard>
          </Padded>
        )}

        {showWhere && (
          <Padded>
            <SectionCard title={t('view.sections.whereImAt')}>
              <Flex direction="column" gap="4">
                {relationshipStatus.length > 0 && (
                  <FieldGroup label={t('view.fields.status')}>
                    <PillRow values={relationshipStatus} />
                  </FieldGroup>
                )}
                {relationshipTypes.length > 0 && (
                  <FieldGroup label={t('view.fields.relationshipType')}>
                    <PillRow values={relationshipTypes} />
                  </FieldGroup>
                )}
              </Flex>
            </SectionCard>
          </Padded>
        )}

        <ProfilePhoto
          src={photoAfterWhere?.url}
          name={user.name}
          index={3}
          overlay={photoAfterWhere && renderLikeOverlay?.(photoAfterWhere)}
        />

        {showWorld && (
          <Padded>
            <SectionCard title={t('view.sections.myWorld')}>
              <Flex direction="column" gap="4">
                {interests.length > 0 && (
                  <FieldGroup label={t('view.fields.interests')}>
                    <PillRow values={interests} />
                  </FieldGroup>
                )}
                {spots.length > 0 && (
                  <FieldGroup label={t('view.fields.spots')}>
                    <PillRow values={spots} />
                  </FieldGroup>
                )}
                {kinks.length > 0 && (
                  <FieldGroup label={t('view.fields.kinks')}>
                    <PillRow values={kinks} />
                  </FieldGroup>
                )}
                {languages.length > 0 && (
                  <FieldGroup label={t('view.fields.languages')}>
                    <PillRow values={languages} />
                  </FieldGroup>
                )}
                {ethnicity.length > 0 && (
                  <FieldGroup label={t('view.fields.ethnicity')}>
                    <PillRow values={ethnicity} />
                  </FieldGroup>
                )}
              </Flex>
            </SectionCard>
          </Padded>
        )}

        <ProfilePhoto
          src={photoAfterWorld?.url}
          name={user.name}
          index={4}
          overlay={photoAfterWorld && renderLikeOverlay?.(photoAfterWorld)}
        />

        {showBasics && (
          <Padded>
            <SectionCard title={t('view.sections.basics')}>
              <Flex direction="column" gap="4">
                {city.length > 0 && (
                  <FieldGroup label={t('view.fields.livesIn')}>
                    <PillRow values={city} />
                  </FieldGroup>
                )}
                {height.length > 0 && (
                  <FieldGroup label={t('view.fields.height')}>
                    <PillRow values={height} />
                  </FieldGroup>
                )}
                {starSign && (
                  <FieldGroup label={t('view.fields.starSign')}>
                    <PillRow values={[starSign]} />
                  </FieldGroup>
                )}
              </Flex>
            </SectionCard>
          </Padded>
        )}

        <ProfilePhoto
          src={photoAtEnd?.url}
          name={user.name}
          index={5}
          overlay={photoAtEnd && renderLikeOverlay?.(photoAtEnd)}
        />

        {!isOwn && onMessage && (
          <Padded>
            <Button type="button" color="gray" highContrast fullWidth size="lg" onClick={onMessage}>
              {t('view.actions.sendMessage')}
            </Button>
          </Padded>
        )}
      </Flex>
    </Flex>
  );
}
